import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { DateTime } from 'luxon'

import { faUpload, faSparkles } from '@fortawesome/pro-light-svg-icons'

import {
  Box, SidebarModal, TabBar, Text, Toggle,
} from '@campaignhub/suit-theme'

import { useSetState } from '@campaignhub/react-hooks'

import { getUppyOptions, getComponentCallbacks } from '@functions/uploadModal'
import { assetTypesForServiceGroup, deriveAssetTypeKey } from '@functions/assetType'
import { isFeatureEnabled } from '@functions/features'

import useUppyUploadVersion from '@hooks/useUppyUploadVersion'
import useReduxAction from '@hooks/useReduxAction'
import useAiVersionGeneration from '@hooks/useAiVersionGeneration'
import useAiAssetGeneration from '@hooks/useAiAssetGeneration'

import AiGenerationTab from './components/AiGenerationTab'
import GeneratingTile from './components/GeneratingTile'
import UploadTab from './components/UploadTab'
import VersionList from './components/VersionList'

const defaultState = {
  activeTab: 'upload',
  asNewAsset: false,
  allowedFileTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/DNG', 'image/CR2', 'image/ARW'],
  assetTypeOptions: [],
  filteredFileTypes: [],
  selectedAssetType: '',
  selectedFileType: 'image',
  typeResolved: false,
}

const assetFileTypes = {
  image: {
    id: 'image',
    description: 'Image',
    fileTypes: ['image/jpeg', 'image/jpg'],
    promptFileType: 0,
  },
  pdf: {
    id: 'pdf',
    description: 'PDF',
    fileTypes: ['application/pdf'],
    promptFileType: 1,
  },
  video: {
    id: 'video',
    description: 'Video',
    fileTypes: ['video/mp4'],
    promptFileType: 2,
  },
}

const UploadVersionModal = (props) => {
  const {
    callbacks, initialActiveTab = 'upload', modalKey = 'UploadVersionModal', serviceJob, showModal, title, titleSecondLine,
    serviceJobOptions, type, displayAllowedFileTypes,
  } = props

  const entities = useSelector(reduxState => reduxState.entities)
  const { assetTypes, serviceGroups, fileServiceContainers } = entities

  const assetTypeList = Object.values(assetTypes || {})

  const { updateDirectFn } = callbacks

  const [state, setState] = useSetState(defaultState)
  const {
    activeTab, allowedFileTypes, assetTypeOptions, asNewAsset, filteredFileTypes, selectedAssetType, typeResolved,
  } = state

  const containerName = `service-jobs-${DateTime.fromISO(serviceJob.createdAt, { zone: 'utc' }).toFormat('yyyy')}`

  const { loading } = useReduxAction('fileServiceContainers', 'loadFileServiceContainer', containerName, [serviceJob])

  const fileServiceContainer = Object.values(fileServiceContainers).find(x => x.container === containerName)

  const assetId = serviceJobOptions?.asset?.id

  // CH-4304: prefills the new asset's name input with the base's file name.
  const defaultAssetName = serviceJobOptions?.asset?.file?.displayName
    || serviceJobOptions?.asset?.file?.originalName
    || serviceJobOptions?.asset?.description
    || ''

  const aiRetouchingEnabled = isFeatureEnabled('ai-retouching')

  const { data: assetPrompts = [], loading: promptsLoading } = useReduxAction(
    'prompts',
    'loadAssetPrompts',
    assetId,
    [assetId],
    { shouldPerformFn: entityReducer => aiRetouchingEnabled && !!assetId && !entityReducer.loading },
  )

  const promptFileType = filteredFileTypes[0]?.promptFileType
  const isImageAsset = filteredFileTypes[0]?.id === assetFileTypes.image.id

  const canUpload = filteredFileTypes.length > 0

  const { generating, generate, stopGenerating } = useAiVersionGeneration(assetId)

  // CH-4304: the same AI tab can mint a NEW asset from this asset (the base is implicit).
  // 'View asset' in the completion swal closes this modal before the page redirects.
  const {
    generating: generatingAsset, generate: generateAsset, stopGenerating: stopGeneratingAsset,
  } = useAiAssetGeneration(serviceJob.id, assetId, { onViewAsset: callbacks.closeModal })

  const uppyCallbacks = { ...getComponentCallbacks('uppy', setState), updateDirectFn }
  const selectedAssetTypeId = assetTypeOptions.find(option => option.key === selectedAssetType)?.id
  const uppyOptions = getUppyOptions(type, allowedFileTypes, serviceJobOptions, uppyCallbacks, 0, 0, fileServiceContainer, undefined, selectedAssetType, selectedAssetTypeId)
  const uppy = useUppyUploadVersion(uppyOptions)

  useEffect(() => {
    if (type === 'Assets'){
      const groupName = serviceGroups[serviceJob.serviceGroupId]?.name
      const groupAssetTypes = assetTypesForServiceGroup(groupName, assetTypeList)

      // Default the folder/type to the asset's current type so a re-uploaded version keeps its
      // category; deriveAssetTypeKey falls back to the file type / service group when the asset
      // has none. typeResolved gates the uploader so a dropped file isn't auto-uploaded before
      // the default is set.
      const defaultKey = deriveAssetTypeKey(serviceJobOptions?.asset, groupName)
      const selectedAssetType = groupAssetTypes.some(option => option.key === defaultKey)
        ? defaultKey
        : (groupAssetTypes[0]?.key || '')

      setState({ assetTypeOptions: groupAssetTypes, selectedAssetType, typeResolved: true })

      if (['Floorplan'].includes(serviceGroups[serviceJob.serviceGroupId]?.name)){
        setState({ filteredFileTypes: Object.values(assetFileTypes).filter(fileType => fileType.id === 'pdf') })
      }
      if (['Photography', 'Virtual Staging'].includes(serviceGroups[serviceJob.serviceGroupId]?.name)){
        setState({ filteredFileTypes: Object.values(assetFileTypes).filter(fileType => fileType.id === 'image') })
      }
      if (['Video', 'VidMo'].includes(serviceGroups[serviceJob.serviceGroupId]?.name)){
        setState({ filteredFileTypes: Object.values(assetFileTypes).filter(fileType => fileType.id === 'video') })
      }
    }
  }, [serviceJob, assetTypeList.length])

  useEffect(() => {
    if (filteredFileTypes.length){
      setState({
        allowedFileTypes: filteredFileTypes[0].fileTypes,
        selectedFileType: filteredFileTypes[0].id,
      })
    }
  }, [filteredFileTypes])

  useEffect(() => {
    if (showModal){
      setState({ activeTab: initialActiveTab })
    } else {
      stopGenerating()
      stopGeneratingAsset()
    }
  }, [showModal, initialActiveTab])

  return (
    <SidebarModal
      callbacks={callbacks}
      modalKey={modalKey}
      showModal={showModal}
      size="small"
    >
      <SidebarModal.Header callbacks={callbacks} title={title} titleSecondLine={titleSecondLine} />

      <SidebarModal.Content hasFooter={false}>
        {canUpload && (
        <Box flexDirection="column" marginBottom="large">
          <TabBar
            items={[
              { key: 'upload', title: 'Upload', icon: faUpload, onClick: () => setState({ activeTab: 'upload' }) },
              ...(aiRetouchingEnabled && isImageAsset
                ? [{ key: 'ai', title: 'AI Generation', icon: faSparkles, onClick: () => setState({ activeTab: 'ai' }) }]
                : []),
            ]}
            selectedItemKey={activeTab}
            invertColors
            style={{ backgroundColor: '#f8f8f8' }}
          />

          <Box
            flexDirection="column"
            backgroundColor="white"
            border="1px solid"
            borderColor="lineColor"
            borderRadius="0 0 5px 5px"
            paddingX="large"
            paddingBottom="large"
          >
            {activeTab === 'upload' && (
              <UploadTab
                assetTypeOptions={assetTypeOptions}
                displayAllowedFileTypes={displayAllowedFileTypes}
                filteredFileTypes={filteredFileTypes}
                loading={loading}
                selectedAssetType={selectedAssetType}
                type={type}
                typeResolved={typeResolved}
                uppy={uppy}
                callbacks={{ setSelectedAssetType: value => setState({ selectedAssetType: value }) }}
              />
            )}

            {activeTab === 'ai' && aiRetouchingEnabled && isImageAsset && (
              <>
                <Toggle
                  boxProps={{ marginTop: 'large' }}
                  disabled={generating || generatingAsset}
                  onClick={() => setState({ asNewAsset: !asNewAsset })}
                  toggleOffColor="bodyFontColor"
                  toggleOffText="New version"
                  toggleOn={asNewAsset}
                  toggleOnText="New asset"
                />

                <AiGenerationTab
                  assetPrompts={assetPrompts}
                  asNewAsset={asNewAsset}
                  defaultName={defaultAssetName}
                  generating={asNewAsset ? generatingAsset : generating}
                  promptFileType={promptFileType}
                  promptsLoading={promptsLoading}
                  callbacks={{ generate: params => (asNewAsset ? generateAsset(params) : generate(params)) }}
                />
              </>
            )}
          </Box>
        </Box>
        )}
        <Text marginBottom="large" fontSize="small" fontWeight="500">
          Versions
        </Text>
        {generating && !asNewAsset && <GeneratingTile />}
        {generatingAsset && asNewAsset && <GeneratingTile messages={['Generating new asset...', 'Please wait...']} />}
        <VersionList />
      </SidebarModal.Content>
    </SidebarModal>
  )
}

UploadVersionModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  displayAllowedFileTypes: PropTypes.bool.isRequired,
  initialActiveTab: PropTypes.string,
  modalKey: PropTypes.string,
  serviceJob: PropTypes.object,
  serviceJobOptions: PropTypes.object.isRequired,
  showModal: PropTypes.bool,
  title: PropTypes.string,
  titleSecondLine: PropTypes.string,
  type: PropTypes.string.isRequired,
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <UploadVersionModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
